// What a wall slot is CALLED.
//
// ⚠️ THE OUTPUT NAME ALONE NAMES NOTHING. Half the switches in a building ship
// as "Light1". A wall sheet has no room list and no breadcrumb above it — it is
// a bare grid on a wall — so a tile reading "Light1" tells the person standing
// in front of it nothing at all, and two of them tell it twice.
//
// The label is therefore built from what the button POINTS AT:
//
//     Room · Device · Button
//
// with two parts left out on purpose:
//
//   - THE LOCATION, always. The whole sheet belongs to one location; repeating
//     it on all twenty-four tiles spends the width and says nothing.
//   - THE ROOM, when the sheet only covers one. A panel that is entirely the
//     Office does not need "Office" on every tile; a panel that mixes Office
//     and Bedroom does. `showRoom` decides, and it is a property of the WHOLE
//     PANEL, not of one button — which is why labels are recomputed for the
//     whole sheet at once (`relabelPanel`) rather than fixed when a button is
//     saved.
//
// Devices with no room (RoomName null — "common devices", RoomId 0) do not
// count towards that tally: they are the location's, not a room's.
//
// ── Why stored labels get rewritten ────────────────────────────────────────
// The label is a COLUMN (WallButton.ButtonText), because the kiosk must render
// instantly from one panel fetch and cannot go looking up device and output
// names for every tile at boot. So the sentence is composed here, on the setup
// screen, and written down.
//
// That means an already-saved label can be out of date — and the first release
// of the wizard stored the bare output name, which is exactly the "Light1"
// problem. `isAutoLabel` recognises anything this app could have generated so
// those rows can be rebuilt, while a caption the OWNER typed is left alone. The
// test is deliberately generous: rebuilding a label that happened to match is
// harmless (it comes back the same or better), whereas failing to recognise one
// leaves the tile unreadable, which is the bug being fixed.

export const SEP = ' · ';

const clean = (v) => String(v ?? '').trim();

/**
 * Join the parts that are present, dropping blanks and repeats.
 *
 * A device named the same as its only output ("Fan" / "Fan") must not say it
 * twice, and a common device with no room reads "Gate · Open" rather than
 * " · Gate · Open".
 */
export function composeLabel({ roomName, deviceName, outputName } = {}, { showRoom = true } = {}) {
  const parts = [showRoom ? roomName : '', deviceName, outputName]
    .map(clean)
    .filter(Boolean);
  const seen = new Set();
  return parts
    .filter((p) => {
      const k = p.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .join(SEP);
}

/**
 * Every caption this app could have written for these parts.
 *
 * Both the room-shown and room-hidden forms, and every shorter combination the
 * older code produced — including the bare output name, which is the label the
 * first version stored and the reason this function exists.
 */
export function autoLabelCandidates(parts = {}) {
  const room = clean(parts.roomName);
  const device = clean(parts.deviceName);
  const output = clean(parts.outputName);
  const out = new Set();
  // Every ordered subset of [room, device, output].
  for (let mask = 1; mask < 8; mask += 1) {
    const label = composeLabel(
      {
        roomName: mask & 1 ? room : '',
        deviceName: mask & 2 ? device : '',
        outputName: mask & 4 ? output : '',
      },
      { showRoom: true },
    );
    if (label) out.add(label);
  }
  return [...out];
}

/** Is `text` something we generated, rather than something the owner typed? */
export function isAutoLabel(text, parts) {
  const t = clean(text).toLowerCase();
  if (!t) return true; // nothing stored: ours to fill in
  return autoLabelCandidates(parts).some((c) => c.toLowerCase() === t);
}

/**
 * Does this sheet span more than one room?
 *
 * Counts distinct room names, ignoring the roomless (common) devices — a sheet
 * of Office switches plus the front gate is still one room.
 */
export function spansManyRooms(partsList = []) {
  const rooms = new Set(
    partsList.map((p) => clean(p?.roomName).toLowerCase()).filter(Boolean),
  );
  return rooms.size > 1;
}

/**
 * Recompute the captions for a whole sheet.
 *
 * `partsFor(button)` -> { roomName, deviceName, outputName }.
 *
 * -> [{ WallButtonId, ButtonText }] for the buttons that actually CHANGED, so
 *    the caller writes only those rows. Returning every button would rewrite
 *    the whole sheet on every save, including the owner's own captions.
 */
export function relabelPanel(buttons = [], partsFor = () => ({})) {
  const list = (buttons || []).map((b) => ({ button: b, parts: partsFor(b) || {} }));
  const showRoom = spansManyRooms(list.map((e) => e.parts));
  const changes = [];
  for (const { button, parts } of list) {
    if (!isAutoLabel(button.ButtonText, parts)) continue; // the owner's words
    const next = composeLabel(parts, { showRoom });
    // An empty result means nothing could be resolved — a device that has since
    // been removed, say. Keep whatever is on the tile rather than blanking it.
    if (!next || next === clean(button.ButtonText)) continue;
    changes.push({ WallButtonId: button.WallButtonId, ButtonText: next });
  }
  return changes;
}
